import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { TaskEntityType, TaskParentType, TaskResponse, UpdateTaskRequest } from "./tasksApi";
import { completeTask as completeTaskApi, deleteTask, updateTask } from "./tasksApi";
import { isAxiosError } from "axios";
import { entityTaskQueryKey, isTasksQueryKey } from "./taskQuery";

export const useTask = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const deleteTaskMutation = useMutation<
    void,
    unknown,
    { taskId: string; taskParentType: TaskParentType; parentId: string }
  >({
    mutationFn: ({ taskId, taskParentType, parentId }) =>
      deleteTask(taskParentTypeToEntityType(taskParentType), parentId, taskId),
    onSuccess: async (_, { taskParentType, parentId }) => {
      enqueueSnackbar("Zadanie zostało usunięte", { variant: "success" });
      await queryClient.invalidateQueries({
        queryKey: entityTaskQueryKey(taskParentTypeToEntityType(taskParentType), parentId),
      });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć zadania. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const completeTaskMutation = useMutation<
    TaskResponse,
    unknown,
    { taskParentType: TaskParentType; parentId: string; taskId: string; note?: string }
  >({
    mutationFn: ({ taskParentType, parentId, taskId, note }) =>
      completeTaskApi(taskParentTypeToEntityType(taskParentType), parentId, taskId, note),
    onSuccess: async (_, { parentId, taskParentType }) => {
      enqueueSnackbar("Zadanie oznaczone jako wykonane", { variant: "success" });
      await queryClient.invalidateQueries({
        queryKey: entityTaskQueryKey(taskParentTypeToEntityType(taskParentType), parentId),
      });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error?.response?.status : undefined;
      if (status === 409) {
        enqueueSnackbar("To zadanie jest już oznaczone jako wykonane", { variant: "warning" });
      } else {
        enqueueSnackbar(`Nie udało się oznaczyć zadania. Powód: ${(error as Error).message}`, { variant: "error" });
      }
    },
  });

  const updateTaskMutation = useMutation<
    TaskResponse,
    unknown,
    { taskParentType: TaskParentType; parentId: string; taskId: string; request: UpdateTaskRequest }
  >({
    mutationFn: ({ taskParentType, parentId, taskId, request }) =>
      updateTask(taskParentTypeToEntityType(taskParentType), parentId, taskId, request),
    onSuccess: async (_, { parentId, taskParentType }) => {
      enqueueSnackbar("Zadanie zostało zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({
        queryKey: entityTaskQueryKey(taskParentTypeToEntityType(taskParentType), parentId),
      });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować zadania. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    deleteTask: (taskParentType: TaskParentType, parentId: string, taskId: string) =>
      deleteTaskMutation.mutateAsync({ taskParentType, parentId, taskId }),
    completeTask: (taskParentType: TaskParentType, parentId: string, taskId: string, note?: string) =>
      completeTaskMutation.mutateAsync({ taskParentType, parentId, taskId, note }),
    updateTask: (taskParentType: TaskParentType, parentId: string, taskId: string, request: UpdateTaskRequest) =>
      updateTaskMutation.mutateAsync({ taskParentType, parentId, taskId, request }),
  };
};

function taskParentTypeToEntityType(parentType: TaskParentType): TaskEntityType {
  switch (parentType) {
    case "CALCULATION":
      return "calculations";
    case "ORDER":
      return "orders";
    case "CUSTOMER":
      return "customers";
  }
}
